import React from "react"
import type { ReactNode } from "react"
import style from "./Titles.module.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import type { IconProp, SizeProp } from "@fortawesome/fontawesome-svg-core"
import GetSpace from "../../utils/space"
import Animate, { AnimationTypes } from "../Animate/Animate"

interface TitlePageProps {
  title?: string
  subtitle?: string
  path?: string
  color?: string
  iconColor?: string
  icon?: IconProp
  boxShadow?: string
  keyCode?: string | number
  sizeProp?: SizeProp
  delay?: string
  animationTypes?: keyof typeof AnimationTypes
  animationDuration?: string
}

interface TitlePageResponsiveProps extends TitlePageProps {
  children?: ReactNode
  subtitleChildren?: ReactNode
}

interface TitlePageResponsiveIconProps extends TitlePageResponsiveProps {
  icon?: IconProp
}

export const TitlePage: React.FC<TitlePageProps> = ({ icon, iconColor, title, subtitle, keyCode: key }) => {
  return (
    <div className={style.containerGlobal} key={key}>
      <div className={style.titles}>
        {icon && <FontAwesomeIcon color={iconColor} className={style.iconsTitlee} icon={icon} />}

        <h1>{title} /</h1>

        {subtitle && <h4>{subtitle}</h4>}
        <hr />
      </div>
    </div>
  )
}

export const TitlePageResponsive: React.FC<TitlePageResponsiveProps> = ({
  title,
  subtitle,
  path,
  children,
  keyCode: key,
}) => {
  return (
    <>
      <div key={key} className={style.containerGlobalResponsive}>
        <div className={style.divTitle}>
          <div className={style.titles}>
            <h1>{title} /</h1>

            {subtitle && <h4>{subtitle}</h4>}
          </div>

          <div className={style.cantainerPath}>{path && <h6>{path}</h6>}</div>
        </div>
      </div>

      {children && <div>{children}</div>}
    </>
  )
}

export const TitlePageResponsiveIcon: React.FC<TitlePageResponsiveIconProps> = ({
  title,
  subtitle,
  path,
  icon,
  children,
  keyCode,
  iconColor,
  delay = "0s",
  animationTypes = AnimationTypes.fadeIn,
  animationDuration = "1s",
}) => {
  return (
    <div key={keyCode} className={style.containerGlobalResponsive}>
      <div className={style.divTitle}>
        <div className={style.titles}>
          {icon && <FontAwesomeIcon color={iconColor} className={style.iconsTitlee} icon={icon} />}

          <h1>{title}</h1>

          {subtitle && <h4>{subtitle}</h4>}
        </div>
        <div className={style.cantainerPath}>{path && <h6>{path}</h6>}</div>
      </div>

      {children && <div>{children}</div>}
    </div>
  )
}

export const TitlePageResponsiveIconSmall: React.FC<TitlePageResponsiveIconProps> = ({
  title,
  subtitle,
  path,
  icon,
  keyCode,
  children,
  sizeProp = "1x",
  subtitleChildren,
  color,
  iconColor,
  boxShadow,
  delay = "0s",
  animationTypes = AnimationTypes.fadeInTraslate,
  animationDuration = "1s",
}) => {
  return (
    <Animate type={animationTypes} duration={animationDuration} delay={delay}>
      <div
        key={keyCode}
        className={style.containerGlobalExpansive}
        style={{ boxShadow: boxShadow, backgroundColor: color }}
      >
        <div className={style.divTitle}>
          <div className={style.titlesSmall}>
            <div className={style.onlyTitle}>
              {icon && <FontAwesomeIcon color={iconColor ?? "black"} size={sizeProp} icon={icon} />}

              <GetSpace left={10} />

              {title && <h1>{title}</h1>}

              {subtitle && <h4>{subtitle}</h4>}
            </div>

            {subtitleChildren}
          </div>

          <div className={style.cantainerPathSmall}>{path && <h6>{path}</h6>}</div>
        </div>

        <GetSpace left={5} />

        {children && <div className={style.buttonsData}>{children}</div>}
      </div>
    </Animate>
  )
}

export const CardMaxHeight: React.FC<TitlePageResponsiveIconProps> = ({
  title,
  subtitle,
  path,
  icon,
  keyCode,
  children,
  subtitleChildren,
  color,
  iconColor,
  delay = "0s",
  animationTypes = AnimationTypes.fadeInTraslate,
  animationDuration = "1s",
}) => {
  return (
    <Animate type={animationTypes} duration={animationDuration} delay={delay}>
      <div key={keyCode} className={style.containerGlobalExpansive} style={{ backgroundColor: color, height: "100%" }}>
        <div className={style.divTitle}>
          <div className={style.titlesSmall}>
            <div className={style.onlyTitle}>
              {icon && <FontAwesomeIcon color={iconColor ?? "black"} className={style.iconsTitlee} icon={icon} />}

              {title && <h1>{title}</h1>}

              {subtitle && <h4>{subtitle}</h4>}
            </div>

            {subtitleChildren}
          </div>

          <div className={style.cantainerPathSmall}>{path && <h6>{path}</h6>}</div>
        </div>

        <GetSpace left={5} />

        {children && <div className={style.buttonsData}>{children}</div>}
      </div>
    </Animate>
  )
}

export const TitleChildrenPath: React.FC<TitlePageResponsiveIconProps> = ({
  title,
  subtitle,
  path,
  icon,
  keyCode,
  children,
  color,
  iconColor,
  boxShadow,
  subtitleChildren,
  sizeProp,
  delay = "0s",
  animationTypes = AnimationTypes.fadeInTraslate,
  animationDuration = "1s",
}) => {
  return (
    <Animate type={animationTypes} duration={animationDuration} delay={delay}>
      <div
        key={keyCode}
        className={style.containerGlobalExpansive}
        style={{ backgroundColor: color, boxShadow: boxShadow }}
      >
        <div className={style.divTitle}>
          <div className={style.titlesSmall1}>
            <div className={style.baseTitle}>
              {icon && <FontAwesomeIcon color={iconColor} className={style.iconsTitlee} icon={icon} size={sizeProp} />}

              <h1>{title}</h1>

              {subtitle && <h4>{subtitle}</h4>}
            </div>

            {subtitleChildren && <div className={style.buttonsData}>{subtitleChildren}</div>}
          </div>

          <div className={style.cantainerPathSmall}>
            {path && <h6>{path}</h6>}
            {children && <div>{children}</div>}
          </div>
        </div>
      </div>
    </Animate>
  )
}

export const CardWithImage: React.FC<TitlePageResponsiveIconProps> = ({
  children,
  subtitleChildren,
  color = "white",
  delay = "0s",
  animationTypes = AnimationTypes.fadeInTraslate,
  animationDuration = "1s",
}) => {
  return (
    <Animate type={animationTypes} duration={animationDuration} delay={delay}>
      <div
        className="
          flex items-start justify-between
          w-full
          rounded-xl
          overflow-hidden
        "
        style={{ backgroundColor: color }}
      >
        <div className="flex items-center gap-1 w-full">
          {subtitleChildren}

          <div className="flex-1">{children}</div>
        </div>
      </div>
    </Animate>
  )
}
